import React, { useState } from "react";
import PrimaryButton from "../../ui/PrimaryButton";
import { ChevronDown, ChevronLeft, X } from "lucide-react";
import { Customer } from "../../../types/customer";
import { useNavigate } from "react-router-dom";
import { useGames } from "../../../hooks/useGames";
import { useGameTables } from "../../../hooks/useGameTables";
import { DateRangeFilter } from "./DateRangeFilter";

interface CustomerHeaderProps {
  customer: Customer;
  activeTab?: number;
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  onDateChange?: (date: string) => void;
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
  onDateClear?: () => void;
  selectedGames?: string[];
  onGamesChange?: (gameIds: string[]) => void;
  selectedTables?: string[];
  onTablesChange?: (tableIds: string[]) => void;
  onDetailsClick?: () => void;
  financials?: {
    totalCashIn: number;
    totalCashOut: number;
    totalBetAmount: number;
    totalWin: number;
    totalLoss: number;
  };
}

export const CustomerHeader: React.FC<CustomerHeaderProps> = ({
  customer,
  activeTab = 0,
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  onDateChange,
  onDateRangeChange,
  onDateClear,
  selectedGames = [],
  onGamesChange,
  selectedTables = [],
  onTablesChange,
  onDetailsClick,
  financials,
}) => {
  const navigate = useNavigate();
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { data: gamesData } = useGames({ limit: 100 });
  const { data: tablesData } = useGameTables({ limit: 100 });

  const games = gamesData?.games || [];
  const tables = tablesData?.gameTables || [];

  const getImageSrc = (photo: string | undefined) => {
    if (imageError) {
      return "/default-avatar.png";
    }

    if (!photo) return "/default-avatar.png";

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    return photo.startsWith("/") ? `${baseUrl}${photo}` : `${baseUrl}/${photo}`;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const filteredTables =
    selectedGames.length > 0
      ? tables.filter((table) => selectedGames.includes(table.gameId))
      : tables;

  const getCustomerTypeColor = (type: string) => {
    switch (type) {
      case "vip":
        return "bg-yellow-600 text-yellow-100";
      case "vvip":
        return "bg-purple-600 text-purple-100";
      default:
        return "bg-slate-600 text-slate-100";
    }
  };

  const getCustomerTypeLabel = (type: string) => {
    switch (type) {
      case "vip":
        return "VIP";
      case "vvip":
        return "VVIP";
      default:
        return "Regular";
    }
  };

  const handleGameToggle = (gameId: string) => {
    const newSelectedGames = selectedGames.includes(gameId)
      ? selectedGames.filter((id) => id !== gameId)
      : [...selectedGames, gameId];
    onGamesChange?.(newSelectedGames);
  };

  const handleTableToggle = (tableId: string) => {
    const newSelectedTables = selectedTables.includes(tableId)
      ? selectedTables.filter((id) => id !== tableId)
      : [...selectedTables, tableId];
    onTablesChange?.(newSelectedTables);
  };

  const getSelectedGamesText = () => {
    if (selectedGames.length === 0) return "Filter by Game";
    if (selectedGames.length === 1) {
      const game = games.find((g) => g.id === selectedGames[0]);
      return game?.name || "1 game selected";
    }
    return `${selectedGames.length} games selected`;
  };

  const getSelectedTablesText = () => {
    if (selectedTables.length === 0) return "Filter by Table";
    if (selectedTables.length === 1) {
      const table = tables.find((t) => t.id === selectedTables[0]);
      return table?.name || "1 table selected";
    }
    return `${selectedTables.length} tables selected`;
  };

  return (
    <div className="bg-slate-800 rounded-t-lg p-4 md:p-6 flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
        {/* Avatar and Name */}
        <div className="flex items-center gap-3 md:gap-4">
          <div className="relative">
            <img
              src={getImageSrc(customer.photo)}
              alt="Customer Avatar"
              className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-white object-cover"
              onError={handleImageError}
            />
            <div
              className={`absolute -bottom-1 -right-1 px-2 py-1 rounded-full text-xs font-medium ${getCustomerTypeColor(
                customer.customerType,
              )}`}
            >
              {getCustomerTypeLabel(customer.customerType)}
            </div>
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white">
              {customer.name}
            </h2>
            <div className="space-y-1 text-xs md:text-sm text-slate-300">
              <p>
                <span className="text-slate-400">Email:</span> {customer.email}
              </p>
              {customer.phone && (
                <p>
                  <span className="text-slate-400">Phone:</span>{" "}
                  {customer.phone}
                </p>
              )}
              <p>
                <span className="text-slate-400">Player ID:</span>{" "}
                {customer.playerId}
              </p>
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <PrimaryButton
            onClick={() => navigate("/customers")}
            className="bg-slate-700 text-white px-3 py-2 md:px-4 rounded hover:bg-slate-600 flex items-center gap-2 text-sm md:text-base"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </PrimaryButton>
          <PrimaryButton
            onClick={() => alert("View guest report")}
            className="bg-sky-700 text-white px-3 py-2 md:px-4 rounded hover:bg-sky-600 text-sm md:text-base"
          >
            View guest report
          </PrimaryButton>
          <PrimaryButton
            onClick={onDetailsClick}
            className="bg-green-700 text-white px-3 py-2 md:px-4 rounded hover:bg-green-600 text-sm md:text-base"
          >
            Details
          </PrimaryButton>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6">
        <div>
          <div className="text-xs text-slate-400">Total Bet Amount</div>
          <div className="text-base md:text-lg font-bold text-white">
            {financials?.totalBetAmount.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-400">Win Amount</div>
          <div className="text-base md:text-lg font-bold text-white">
            {financials?.totalWin.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-400">Loss Amount</div>
          <div className="text-base md:text-lg font-bold text-white">
            {financials?.totalLoss.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-400">Cash-in</div>
          <div className="text-base md:text-lg font-bold text-white">
            {financials?.totalCashIn.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-400">Cash-out</div>
          <div className="text-base md:text-lg font-bold text-white">
            {financials?.totalCashOut.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Filters - Conditional based on active tab */}
      <div className="flex flex-wrap gap-2 md:gap-4 mt-2 relative">
        {/* Date Range Filter - Always shown */}
        <DateRangeFilter
          selectedDate={selectedDate}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          onDateChange={onDateChange}
          onDateRangeChange={onDateRangeChange}
          onClear={onDateClear}
        />

        {activeTab === 0 && (
          <>
            {/* Game Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowGameDropdown(!showGameDropdown);
                  setShowTableDropdown(false);
                }}
                className="flex items-center bg-slate-700 rounded px-3 md:px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors min-w-[140px] md:min-w-[180px] text-sm md:text-base"
              >
                <span className="truncate flex-1 text-left">
                  {getSelectedGamesText()}
                </span>
                {selectedGames.length > 0 && (
                  <X
                    className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGamesChange?.([]);
                    }}
                  />
                )}
                <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
              </button>

              {showGameDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 min-w-[250px] max-h-60 overflow-y-auto">
                  <div className="p-2 border-b border-slate-600">
                    <div className="flex justify-between items-center">
                      <span className="text-white text-sm font-medium">
                        Select Games
                      </span>
                      <button
                        onClick={() => onGamesChange?.([])}
                        className="text-xs text-slate-400 hover:text-white"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  {games.map((game) => (
                    <label
                      key={game.id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-600 cursor-pointer text-white text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGames.includes(game.id)}
                        onChange={() => handleGameToggle(game.id)}
                        className="w-4 h-4 text-blue-600 bg-slate-600 border-slate-500 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="flex-1">{game.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Table Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTableDropdown(!showTableDropdown);
                  setShowGameDropdown(false);
                }}
                className="flex items-center bg-slate-700 rounded px-3 md:px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors min-w-[140px] md:min-w-[180px] text-sm md:text-base"
              >
                <span className="truncate flex-1 text-left">
                  {getSelectedTablesText()}
                </span>
                {selectedTables.length > 0 && (
                  <X
                    className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTablesChange?.([]);
                    }}
                  />
                )}
                <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
              </button>

              {showTableDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 min-w-[250px] max-h-60 overflow-y-auto">
                  <div className="p-2 border-b border-slate-600">
                    <div className="flex justify-between items-center">
                      <span className="text-white text-sm font-medium">
                        Select Tables
                      </span>
                      <button
                        onClick={() => onTablesChange?.([])}
                        className="text-xs text-slate-400 hover:text-white"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  {filteredTables.map((table) => (
                    <label
                      key={table.id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-600 cursor-pointer text-white text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTables.includes(table.id)}
                        onChange={() => handleTableToggle(table.id)}
                        className="w-4 h-4 text-blue-600 bg-slate-600 border-slate-500 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="flex-1">{table.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Close dropdowns when clicking outside */}
      {(showGameDropdown || showTableDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowGameDropdown(false);
            setShowTableDropdown(false);
          }}
        />
      )}
    </div>
  );
};
